<template>
  <div class="chat-session-selector">
    <!-- Sidebar Navigation -->
    <div class="candidate-sidebar">
      <nav class="sidebar-nav">
        <div class="nav-group">
          <a
            class="nav-item"
            :class="{ active: isAutomatedAssessmentSection }"
            @click="navigateToSection('automated-assessment')"
          >
            <span>Automated Assessment</span>
          </a>
          <div
            v-if="isAutomatedAssessmentSection"
            class="nav-sub-items"
          >
            <a
              class="nav-sub-item"
              :class="{ active: activeSection === 'automated-assessment' }"
              @click="navigateToSection('automated-assessment')"
            >
              Model
            </a>
            <a
              class="nav-sub-item"
              :class="{ active: activeSection === 'automated-assessment-dashboard', locked: !hasLatestCreditModel }"
              @click="navigateToSection('automated-assessment-dashboard')"
            >
              Dashboard
            </a>
            <a
              class="nav-sub-item"
              :class="{ active: activeSection === 'automated-assessment-report', locked: !hasLatestCreditModel }"
              @click="navigateToSection('automated-assessment-report')"
            >
              Report
            </a>
          </div>
        </div>
        <a 
          @click="navigateToSection('overview')"
          class="nav-item"
          :class="{ active: activeSection === 'overview' }"
        >
          <span>Overview</span>
        </a>
        <a 
          @click="navigateToSection('chat-sessions')"
          class="nav-item"
          :class="{ active: activeSection === 'chat-sessions' }"
        >
          <span>Chat Sessions</span>
        </a>
        <a 
          @click="navigateToSection('kyb')"
          class="nav-item"
          :class="{ active: activeSection === 'kyb' }"
        >
          <span>KYB Documents</span>
        </a>
        <a 
          @click="navigateToSection('vat')"
          class="nav-item"
          :class="{ active: activeSection === 'vat' }"
        >
          <span>VAT Statements</span>
        </a>
        <a 
          @click="navigateToSection('bank-statement')"
          class="nav-item"
          :class="{ active: activeSection === 'bank-statement' }"
        >
          <span>Bank Statements</span>
        </a>
        <a 
          @click="navigateToSection('analysis-pack')"
          class="nav-item"
          :class="{ active: activeSection === 'analysis-pack', locked: !analysisPackNavAvailable }"
        >
          <span>Company Analysis</span>
        </a>
        <a 
          @click="navigateToSection('forecasting')"
          class="nav-item"
          :class="{ active: activeSection === 'forecasting', locked: !forecastingNavAvailable }"
        >
          <span>Forecasting</span>
        </a>
      </nav>
    </div>

    <!-- Main Content Area -->
    <div
      class="candidate-main-content"
      :class="{ 'candidate-main-content--overflow-visible': activeSection === 'forecasting' }"
    >
      <!-- Overview Section -->
      <div v-if="activeSection === 'overview'" class="section-content">
        <!-- Candidate Header -->
        <div class="candidate-header">
          <div class="credit-rating-row">
            <div class="credit-rating-panel">
              <div class="candidate-details">
                <!-- Credit Model Final Score -->
                <div class="credit-model-card credit-model-loading" v-if="creditModelLoading">
                  <div class="credit-model-content">
                    <BaseSpinner />
                  </div>
                </div>

                <div class="credit-model-card error" v-else-if="creditModelError">
                  <div class="credit-model-content">
                    <div class="credit-model-info">
                      <span class="credit-model-label">Credit Assessment</span>
                      <span class="credit-model-status">{{ creditModelError }}</span>
                    </div>
                  </div>
                </div>

                <div class="credit-model-card success" v-else-if="creditModelScore && creditModelScore.Summary">
                  <div class="credit-model-content credit-model-gauge-panel">
                    <div class="credit-model-gauge-card">
                      <div class="credit-model-gauge-body">
                        <div class="credit-model-gauge-info">
                          <div class="credit-model-final-main">
                            <div class="credit-model-final-score">
                              <strong
                                :class="getRiskRatingClass(creditModelScore.Summary.rating, creditModelScore.Summary.totalScore)"
                              >{{ formatCreditScoreNumber(creditModelScore.Summary.rating) }}</strong>
                              <span>/ {{ formatCreditScoreNumber(creditModelScore.Summary.totalScore) }}</span>
                            </div>
                            <span
                              class="risk-rating-badge"
                              :class="getRiskRatingClass(creditModelScore.Summary.rating, creditModelScore.Summary.totalScore)"
                            >{{ getRiskRatingLabel(creditModelScore.Summary.rating, creditModelScore.Summary.totalScore) }}</span>
                          </div>
                        </div>
                        <div
                          class="risk-gauge"
                          role="img"
                          :aria-label="`${getRiskRatingLabel(creditModelScore.Summary.rating, creditModelScore.Summary.totalScore)}: ${formatCreditScorePercentage(creditModelScore.Summary.rating, creditModelScore.Summary.totalScore)}`"
                        >
                          <div class="risk-gauge-dial">
                            <div class="risk-gauge-arc"></div>
                            <div class="risk-gauge-mask"></div>
                            <div
                              class="risk-gauge-needle"
                              :style="{ transform: `rotate(${getRiskGaugeAngle(creditModelScore.Summary.rating, creditModelScore.Summary.totalScore)}deg)` }"
                            ></div>
                            <div class="risk-gauge-hub"></div>
                          </div>
                          <div class="risk-gauge-scale">
                            <span>0</span>
                            <span>100</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="credit-model-details">
                    <template v-for="(category, key) in creditModelScore">
                      <div
                        v-if="key !== 'Summary' && key !== '__ob__'"
                        :key="key"
                        class="credit-model-category"
                      >
                        <div class="category-header">
                          <span class="category-name">{{ key }}</span>
                        </div>
                        <div class="category-scores">
                          <div class="score-item">
                            <span class="score-value">
                              <span class="score-obtained">{{ formatCreditScoreNumber(category.ObtainedScore) }}</span>
                              <span class="score-total"> / {{ formatCreditScoreNumber(category.Weights) }}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <div class="credit-model-card empty" v-else>
                  <div class="credit-model-content">
                    <div class="credit-model-info">
                      <span class="credit-model-label">No Credit Assessment Yet</span>
                      <BaseButton
                        stylevariant="primary"
                        class="credit-model-cta-button"
                        :cus_style="{ width: 'fit-content', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
                        @click="navigateToSection('automated-assessment')"
                      >
                        <i class="las la-play"></i>
                        Run Automated Assessment
                      </BaseButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="view-files-action">
              <BaseButton
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
                @click="viewFiles"
              >
                <i class="las la-folder-open"></i>
                View All Files
              </BaseButton>
            </div>
          </div>
        </div>

    <!-- Org Details Section -->
    <div class="organisation-details-section">
      <div class="sessions-header">
        <h2 class="section-title">Organisation Details</h2>
        <span class="autosave-status" :class="autosaveStatus">
          <template v-if="autosaveStatus === 'saving'">
            <i class="las la-sync la-spin"></i>
            Saving…
          </template>
          <template v-else-if="autosaveStatus === 'saved'">
            <i class="las la-check-circle"></i>
            Saved
          </template>
          <template v-else-if="autosaveStatus === 'error'">
            <i class="las la-exclamation-circle"></i>
            Couldn't save — will retry
          </template>
        </span>
      </div>

      <div v-if="organisationDetailsLoading" class="loading organisation-details-loading">
        <BaseSpinner />
      </div>
      <div v-else-if="organisationDetailsError" class="error-message">{{ organisationDetailsError }}</div>
      <div v-else class="organisation-details-content">
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Business Incorporation Date</label>
            <div class="date-input-wrapper">
              <v-date-picker
                v-model="organisationForm.businessIncorporationDate"
                :masks="dateMasks"
                :select-attribute="dateAttrs"
              >
                <template v-slot="{ inputValue, inputEvents }">
                  <input
                    class="form-input date-input"
                    :value="inputValue"
                    v-on="inputEvents"
                    placeholder="Select incorporation date"
                    readonly
                  />
                </template>
              </v-date-picker>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Company Credit Score</label>
            <input
              type="number"
              v-model="organisationForm.companyCreditScore"
              class="form-input"
              placeholder="Optional"
              step="1"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Owner's Industry Experience (Years)</label>
            <input
              type="number"
              v-model="organisationForm.ownerIndustryExperience"
              class="form-input"
              placeholder="Enter years of experience"
              min="0"
            />
          </div>
        </div>

        <div class="form-grid sector-row">
          <div class="form-group">
            <label class="form-label">Main Sector</label>
            <ChatbotSelect
              v-model="organisationForm.mainSectorType"
              :options="mainSectorTypeOptions"
              placeholder="Select main sector"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Sector</label>
            <ChatbotSelect
              v-model="organisationForm.mainSector"
              :options="mainSectorOptions"
              placeholder="Select main sector"
              :searchable="true"
              @input="onMainSectorChange"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Subsector</label>
            <ChatbotSelect
              v-model="organisationForm.subsector"
              :options="availableSubSectors"
              placeholder="Select subsector"
              :searchable="true"
              :disabled="!organisationForm.mainSector"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Facility Details Section -->
    <div
      id="facility-details"
      ref="facilityDetailsSection"
      class="organisation-details-section"
    >
      <div class="sessions-header">
        <h2 class="section-title">Facility Details</h2>
        <span class="autosave-status" :class="autosaveStatus">
          <template v-if="autosaveStatus === 'saving'">
            <i class="las la-sync la-spin"></i>
            Saving…
          </template>
          <template v-else-if="autosaveStatus === 'saved'">
            <i class="las la-check-circle"></i>
            Saved
          </template>
          <template v-else-if="autosaveStatus === 'error'">
            <i class="las la-exclamation-circle"></i>
            Couldn't save — will retry
          </template>
        </span>
      </div>

      <div v-if="organisationDetailsLoading" class="loading organisation-details-loading">
        <BaseSpinner />
      </div>
      <div v-else-if="organisationDetailsError" class="error-message">{{ organisationDetailsError }}</div>
      <div v-else class="organisation-details-content">
        <div class="form-grid">
            <div class="form-group">
              <label class="form-label">Requested Amount</label>
              <input
                ref="requestedAmountField"
                type="number"
                v-model="organisationForm.requestedAmount"
                class="form-input"
                :class="{ 'field-highlight': highlightRequestedAmount }"
                placeholder="Enter requested amount"
                step="0.01"
                min="0"
              />
            </div>

            <div class="form-group">
              <label class="form-label">Tenure (Months)</label>
              <ChatbotSelect
                v-model="organisationForm.tenure"
                :options="tenureOptions"
                :reduce="option => option.value"
                label="label"
                placeholder="Select tenure"
              />
            </div>

            <div class="form-group">
              <label class="form-label">Rate</label>
              <div class="rate-display-card">
                <template v-if="!organisationForm.tenure">
                  <span class="rate-display-placeholder">
                    Select tenure to view rate
                  </span>
                </template>
                <template v-else-if="derivedRateDisplay">
                  <span class="rate-display-inline">
                    <strong class="rate-display-value">{{ derivedRateDisplay }}</strong>
                    <span class="rate-display-note-inline">applied</span>
                  </span>
                </template>
                <template v-else>
                  <span class="rate-display-placeholder">
                    No rate configured
                  </span>
                </template>
              </div>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">Utilization</label>
              <input
                type="number"
                v-model="organisationForm.utilization"
                class="form-input"
                placeholder="Enter utilization"
                step="0.01"
                min="0"
              />
            </div>

            <div class="form-group">
              <label class="form-label">Cheque Return Due to Insufficient Balance</label>
              <ChatbotSelect
                v-model="organisationForm.chequeReturn"
                :options="chequeReturnOptions"
                placeholder="Select option"
              />
            </div>

            <div class="form-group">
              <label class="aecb-data-toggle">
                <input
                  type="checkbox"
                  v-model="organisationForm.hasAecbData"
                />
                <span class="form-label">Has AECB Data</span>
              </label>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">Scoring Period</label>
              <ChatbotSelect
                v-model="organisationForm.scoringPeriodEndKey"
                :options="scoringPeriodOptions"
                :reduce="option => option.value"
                label="label"
                placeholder="Select 12-month period"
                :searchable="true"
              />
            </div>
          </div>

          <div class="org-debts-section">
            <div class="org-debts-header">
              <label class="form-label">Debts</label>
              <BaseButton
                type="button"
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
                @click="addDebt"
              >
                <i class="las la-plus"></i>
                Add Debt
              </BaseButton>
            </div>

            <div class="debts-table-wrapper">
              <table class="debts-table">
                <thead>
                  <tr>
                    <th>Lender</th>
                    <th>Approval Amount</th>
                    <th>Amount Outstanding</th>
                    <th>Repayment/Interest Service</th>
                    <th class="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="organisationForm.debts.length === 0">
                    <td colspan="5" class="debts-empty-cell">No debts added</td>
                  </tr>
                  <tr
                    v-for="(debt, index) in organisationForm.debts"
                    :key="debt._key"
                    class="debt-row"
                  >
                    <td>
                      <input
                        type="text"
                        v-model="debt.lender"
                        class="debt-input"
                        placeholder="Enter lender name"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputmode="decimal"
                        v-model="debt.approval_amount"
                        class="debt-input"
                        placeholder="0.00"
                        @blur="formatDebtAmountField(debt, 'approval_amount')"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputmode="decimal"
                        v-model="debt.amount_outstanding"
                        class="debt-input"
                        placeholder="0.00"
                        @blur="formatDebtAmountField(debt, 'amount_outstanding')"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputmode="decimal"
                        v-model="debt.repayment_interest_service"
                        class="debt-input"
                        placeholder="0.00"
                        @blur="formatDebtAmountField(debt, 'repayment_interest_service')"
                      />
                    </td>
                    <td class="col-actions">
                      <BaseButton
                        type="button"
                        stylevariant="link"
                        class="remove-debt-btn"
                        :cus_style="{ width: 'auto', padding: '0.25rem', border: 'none' }"
                        :focusable="false"
                        title="Remove debt"
                        @click="removeDebt(index)"
                      >
                        <i class="las la-trash"></i>
                      </BaseButton>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        <div class="org-bank-balances-section">
          <div class="org-bank-balances-header">
            <label class="bank-balance-toggle">
              <input
                type="checkbox"
                v-model="organisationForm.useCustomBankStatementBalances"
                @change="onCustomBankBalanceToggle"
              />
              <span class="form-label">Custom Bank Statement Balances</span>
            </label>
          </div>

          <!-- Custom mode: editable credits input + import / imported balances -->
          <div
            v-if="organisationForm.useCustomBankStatementBalances"
            class="org-bank-balances-fields"
          >
            <div class="form-group bank-credits-field">
              <label class="form-label">Total Bank Statement Credits</label>
              <input
                type="number"
                v-model="organisationForm.totalBankStatementCredits"
                class="form-input"
                placeholder="Enter total credits"
                step="0.01"
                min="0"
              />
            </div>

            <div v-if="!hasImportedBankBalances" class="bank-balances-import">
              <label class="form-label">Import Bank Balances</label>
              <div class="bank-balances-import-row">
                <div class="bank-balances-file-input-container">
                  <input
                    ref="bankBalancesFileInput"
                    type="file"
                    class="bank-balances-file-input"
                    accept=".xlsx,.xls,.csv"
                    @change="onBankBalancesFileChange"
                  />
                  <div class="bank-balances-file-input-overlay">
                    <template v-if="bankBalancesImportFile">
                      <i class="las la-file-excel bank-balances-file-icon"></i>
                      <span class="bank-balances-file-selected">{{ bankBalancesImportFile.name }}</span>
                    </template>
                    <template v-else>
                      <i class="las la-cloud-upload-alt bank-balances-file-icon"></i>
                      <span class="bank-balances-file-hint">Choose .xlsx, .xls, or .csv file</span>
                    </template>
                  </div>
                </div>
                <BaseButton
                  type="button"
                  stylevariant="primary"
                  :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
                  :disabled="!bankBalancesImportFile || bankBalancesImporting"
                  :loading="bankBalancesImporting"
                  @click="importBankBalances"
                >
                  Import
                </BaseButton>
              </div>
            </div>

            <div v-if="hasImportedBankBalances">
              <div class="org-debts-header">
                <label class="form-label">Imported Bank Balances</label>
                <BaseButton
                  type="button"
                  stylevariant="primary"
                  :cus_style="{ width: 'auto', padding: '0.5rem 0.875rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
                  :disabled="bankBalancesDeleting"
                  :loading="bankBalancesDeleting"
                  @click="deleteImportedBankBalances"
                >
                  <i class="las la-trash"></i>
                  Delete Import
                </BaseButton>
              </div>
              <div class="bank-balances-table-wrapper">
                <table class="bank-balances-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Day 7</th>
                      <th>Day 14</th>
                      <th>Day 22</th>
                      <th>Day 30</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in organisationForm.customBankStatementBalances"
                      :key="`${row.year}-${row.month_number}-${index}`"
                    >
                      <td class="month-cell">{{ row.month }}</td>
                      <td>{{ formatBankBalance(row.day_7) }}</td>
                      <td>{{ formatBankBalance(row.day_14) }}</td>
                      <td>{{ formatBankBalance(row.day_22) }}</td>
                      <td>{{ formatBankBalance(row.day_30) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Summary mode: read-only data from bank statement summary API -->
          <div v-else class="org-bank-balances-fields">
            <div v-if="bankStatementSummaryTotalCredits !== null" class="form-group bank-credits-field">
              <label class="form-label">Total Bank Statement Credits</label>
              <span class="form-static-value">{{ formatBankBalance(bankStatementSummaryTotalCredits) }}</span>
            </div>

            <div v-if="bankStatementSummaryBalances.length > 0" class="bank-balances-table-wrapper">
              <label class="form-label">Bank Balances</label>
              <table class="bank-balances-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Day 7</th>
                    <th>Day 14</th>
                    <th>Day 22</th>
                    <th>Day 30</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in bankStatementSummaryBalances"
                    :key="`summary-${row.year}-${row.month_number}-${index}`"
                  >
                    <td class="month-cell">{{ row.month }}</td>
                    <td>{{ formatBankBalance(row.day_7) }}</td>
                    <td>{{ formatBankBalance(row.day_14) }}</td>
                    <td>{{ formatBankBalance(row.day_22) }}</td>
                    <td>{{ formatBankBalance(row.day_30) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="org-erp-section">
          <div class="org-erp-header">
            <label class="form-label">ERP Extract</label>
            <BaseButton
              v-if="!erpExtractDoc"
              stylevariant="primary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
              @click="uploadErpExtract"
            >
              <i class="las la-plus"></i>
              Upload
            </BaseButton>
          </div>
          <div v-if="erpExtractDoc" class="document-card">
            <div class="document-card-content">
              <div class="document-icon-large">
                <i class="las la-file-excel"></i>
              </div>
              <span class="document-name">{{ erpExtractDoc.name }}</span>
              <span class="document-date">
                {{ formatUploadDate(erpExtractDoc.uploaded_at) }}
              </span>
              <span class="status-badge" :class="getStatusClass(erpExtractDoc.status)">
                <i :class="getStatusIcon(erpExtractDoc.status)"></i>
              </span>
              <button
                v-if="erpExtractDoc.status === 'completed' || erpExtractDoc.status === 'success'"
                type="button"
                class="view-btn"
                @click.stop="viewErpExtractDocument"
                title="View Document"
              >
                <i class="las la-external-link-alt"></i>
              </button>
              <button
                type="button"
                class="delete-btn"
                @click.stop="deleteErpExtractDocument"
                title="Delete Document"
              >
                <i class="las la-trash"></i>
              </button>
            </div>
          </div>
          <div v-else class="empty-document">
            <i class="las la-file-excel"></i>
            <p>No ERP Extract uploaded</p>
          </div>
        </div>
      </div>
    </div>

      </div>
      <!-- End Overview Section -->

      <!-- Chat Sessions Section -->
      <div v-if="activeSection === 'chat-sessions'" class="section-content subpage-card">
        <ChatSessionsPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- End Chat Sessions Section -->

      <!-- KYB Documents Section -->
      <div v-if="activeSection === 'kyb'" class="section-content subpage-card">
        <KybPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- End KYB Documents Section -->

      <!-- VAT Statement Section -->
      <div v-if="activeSection === 'vat'" class="section-content subpage-card">
        <VatPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- End VAT Statement Section -->

      <!-- Bank Statement Section -->
      <div v-if="activeSection === 'bank-statement'" class="section-content subpage-card">
        <BankStatementPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- End Bank Statement Section -->

      <!-- Company Analysis Pack Section -->
      <div v-if="activeSection === 'analysis-pack'" class="section-content subpage-card">
        <AnalysisPackPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- End Company Analysis Pack Section -->

      <!-- Automated Assessment — Model -->
      <div v-if="activeSection === 'automated-assessment'" class="section-content subpage-card">
        <AutomatedAssessmentPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- Automated Assessment — Dashboard -->
      <div v-if="activeSection === 'automated-assessment-dashboard'" class="section-content subpage-card">
        <AutomatedAssessmentDashboardPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- Automated Assessment — Report -->
      <div v-if="activeSection === 'automated-assessment-report'" class="section-content subpage-card">
        <AutomatedAssessmentReportPage :candidate-id="selectedCandidate.id" />
      </div>

      <!-- Forecasting Section -->
      <div v-if="activeSection === 'forecasting'" class="section-content subpage-card">
        <ForecastingPage :candidate-id="selectedCandidate.id" />
      </div>
      <!-- End Forecasting Section -->
    </div>
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import { MAIN_SECTORS, SECTOR_TO_SUB_SECTORS } from '@/constants';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';
import ChatbotSelect from './ChatbotSelect.vue';
import KybPage from "../kyb.vue";
import VatPage from "../vat.vue";
import BankStatementPage from "../bankStatement.vue";
import AnalysisPackPage from "../analysisPack.vue";
import AutomatedAssessmentPage from "../automatedAssessmentInput.vue";
import AutomatedAssessmentReportPage from "../automatedAssessmentReport.vue";
import AutomatedAssessmentDashboardPage from "../automatedAssessmentDashboard.vue";
import ForecastingPage from "../forecasting.vue";
import ChatSessionsPage from "../chatSessions.vue";

export default {
  name: 'CandidateDetails',
  components: {
    BaseButton,
    BaseSpinner,
    ChatbotSelect,
    KybPage,
    VatPage,
    BankStatementPage,
    AnalysisPackPage,
    AutomatedAssessmentPage,
    AutomatedAssessmentReportPage,
    AutomatedAssessmentDashboardPage,
    ForecastingPage,
    ChatSessionsPage,
  },
  props: {
    selectedCandidate: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeSection: 'overview',
      creditModelScore: null,
      creditModelLoading: false,
      creditModelError: null,
      hasLatestCreditModel: false,
      organisationDetailsLoading: true,
      organisationDetailsSaving: false,
      organisationDetailsError: null,
      organisationDetailsRaw: {},
      highlightRequestedAmount: false,
      highlightRequestedAmountTimer: null,
      autosaveStatus: 'idle',
      autosaveTimer: null,
      suppressAutosave: true,
      autosaveResaveQueued: false,
      erpExtractDoc: null,
      bankBalancesImportFile: null,
      bankBalancesImporting: false,
      bankBalancesDeleting: false,
      bankStatementSummary: null,
      sectionNavDocs: {
        tradeLicense: false,
        emiratesId: false,
        bankStatements: false,
        analysisPack: false
      },
      debtKeyCounter: 0,
      organisationForm: {
        businessIncorporationDate: null,
        requestedAmount: '',
        tenure: '',
        utilization: '',
        rate: '',
        scoringPeriodEndKey: '',
        ownerIndustryExperience: '',
        chequeReturn: '',
        companyCreditScore: '',
        hasAecbData: false,
        mainSectorType: '',
        mainSector: '',
        subsector: '',
        debts: [],
        useCustomBankStatementBalances: false,
        customBankStatementBalances: [],
        totalBankStatementCredits: ''
      },
      dateMasks: {
        input: 'DD-MMM-YYYY',
      },
      dateAttrs: {
        highlight: {
          start: {
            style: { backgroundColor: '#0059BE' },
            contentStyle: { color: '#ffffff' },
          },
        }
      }
    }
  },
  computed: {
    tenureOptions() {
      return [
        { label: '1', value: '1' },
        { label: '1.5', value: '1.5' },
        { label: '2', value: '2' },
        { label: '3', value: '3' }
      ];
    },
    mainSectorTypeOptions() {
      return ['Trading', 'Manufacturing', 'Services'];
    },
    mainSectorOptions() {
      return MAIN_SECTORS;
    },
    availableSubSectors() {
      if (!this.organisationForm.mainSector) return [];
      return SECTOR_TO_SUB_SECTORS[this.organisationForm.mainSector] || [];
    },
    chequeReturnOptions() {
      return [0, 1, 2];
    },
    derivedRateDisplay() {
      const value = this.organisationForm.rate;
      if (value === '' || value === null || value === undefined) {
        return '';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return String(value);
      }

      return `${numericValue}%`;
    },
    scoringPeriodOptions() {
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      const options = [];
      const now = new Date();
      const latestEnd = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      for (let i = 0; i < 36; i += 1) {
        const end = new Date(latestEnd.getFullYear(), latestEnd.getMonth() - i, 1);
        const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
        options.push({
          id: `${end.getFullYear()}-${end.getMonth() + 1}`,
          value: `${end.getFullYear()}-${end.getMonth() + 1}`,
          label: `${monthNames[start.getMonth()]} ${start.getFullYear()} – ${monthNames[end.getMonth()]} ${end.getFullYear()}`
        });
      }

      return options;
    },
    hasImportedBankBalances() {
      return this.organisationForm.customBankStatementBalances.some((row) =>
        ['day_7', 'day_14', 'day_22', 'day_30'].some((key) =>
          row[key] !== '' && row[key] !== null && row[key] !== undefined
        )
      );
    },
    bankStatementSummaryTotalCredits() {
      const months = this.bankStatementSummary?.total_credits_by_month;
      if (!Array.isArray(months) || months.length === 0) return null;

      const total = months.reduce((sum, m) => sum + (Number(m.total_credits) || 0), 0);
      return total > 0 ? total : null;
    },
    bankStatementSummaryBalances() {
      const rows = this.bankStatementSummary?.combined_balances;
      if (!Array.isArray(rows) || rows.length === 0) return [];

      return rows.map((row) => this.normalizeBankBalanceRow(row));
    },
    analysisPackNavAvailable() {
      const hasBankData = this.sectionNavDocs.bankStatements || this.hasImportedBankBalances;
      return this.sectionNavDocs.tradeLicense
        && this.sectionNavDocs.emiratesId
        && hasBankData;
    },
    forecastingNavAvailable() {
      return this.sectionNavDocs.analysisPack;
    },
    isAutomatedAssessmentSection() {
      return this.activeSection === 'automated-assessment'
        || this.activeSection === 'automated-assessment-dashboard'
        || this.activeSection === 'automated-assessment-report';
    }
  },
  async mounted() {
    // Use nextTick to ensure route is fully resolved
    await this.$nextTick();
    this.determineActiveSection();
    await Promise.all([
      this.loadCreditModelScore(),
      this.loadOrganisationDetails(),
      this.loadSectionNavRequirements()
    ]);
    this.focusRequestedAmountIfNeeded();
  },
  watch: {
    '$route': {
      immediate: false,
      handler() {
        this.$nextTick(() => {
          this.determineActiveSection();
          this.focusRequestedAmountIfNeeded();
        });
      }
    },
    organisationForm: {
      deep: true,
      handler() {
        if (this.suppressAutosave) return;
        this.queueAutosave();
      }
    },
    activeSection(section) {
      if (section === 'analysis-pack' || section === 'forecasting') {
        this.loadSectionNavRequirements();
      }
      if (
        section === 'automated-assessment'
        || section === 'automated-assessment-dashboard'
        || section === 'automated-assessment-report'
      ) {
        this.loadCreditModelAvailability();
      }
      if (section === 'overview') {
        this.focusRequestedAmountIfNeeded();
      }
    },
    organisationDetailsLoading(isLoading) {
      if (!isLoading) {
        this.focusRequestedAmountIfNeeded();
      }
    }
  },
  beforeDestroy() {
    if (this.highlightRequestedAmountTimer) {
      clearTimeout(this.highlightRequestedAmountTimer);
      this.highlightRequestedAmountTimer = null;
    }
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
      if (this.autosaveStatus === 'saving') {
        this.saveOrganisationDetails();
      }
    }
  },
  methods: {
    determineActiveSection() {
      const routeName = this.$route.name;
      const path = this.$route.path;

      if (
        routeName === 'chatbot-candidate'
        || (path && path.includes('/sessions'))
      ) {
        this.activeSection = 'chat-sessions';
      } else if (routeName === 'chatbot-candidate-kyb' || (path && path.includes('/kyb'))) {
        this.activeSection = 'kyb';
      } else if (routeName === 'chatbot-candidate-vat' || (path && path.includes('/vat'))) {
        this.activeSection = 'vat';
      } else if (routeName === 'chatbot-candidate-bank-statement' || (path && path.includes('/bank-statement'))) {
        this.activeSection = 'bank-statement';
      } else if (routeName === 'chatbot-candidate-analysis-pack' || (path && path.includes('/analysis-pack'))) {
        this.activeSection = 'analysis-pack';
      } else if (routeName === 'chatbot-automated-assessment-dashboard' || (path && path.includes('/automated-assessment-dashboard'))) {
        this.activeSection = 'automated-assessment-dashboard';
      } else if (routeName === 'chatbot-automated-assessment-report' || (path && path.includes('/automated-assessment-report'))) {
        this.activeSection = 'automated-assessment-report';
      } else if (routeName === 'chatbot-automated-assessment-input' || (path && path.includes('/automated-assessment'))) {
        this.activeSection = 'automated-assessment';
      } else if (routeName === 'chatbot-candidate-forecasting' || (path && path.includes('/forecasting'))) {
        this.activeSection = 'forecasting';
      } else {
        this.activeSection = 'overview';
      }
    },

    focusRequestedAmountIfNeeded() {
      if (this.$route.query.focus !== 'requested-amount') return;
      if (this.activeSection !== 'overview') return;
      if (this.organisationDetailsLoading) return;

      this.$nextTick(() => {
        const field = this.$refs.requestedAmountField;
        const section = this.$refs.facilityDetailsSection;
        const target = field || section;
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (field && typeof field.focus === 'function') {
          field.focus({ preventScroll: true });
        }

        this.highlightRequestedAmount = true;
        if (this.highlightRequestedAmountTimer) {
          clearTimeout(this.highlightRequestedAmountTimer);
        }
        this.highlightRequestedAmountTimer = setTimeout(() => {
          this.highlightRequestedAmount = false;
          this.highlightRequestedAmountTimer = null;
        }, 2800);

        const query = { ...(this.$route.query || {}) };
        delete query.focus;
        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
          query
        }).catch(() => {});
      });
    },

    async navigateToSection(section) {
      if (section === 'analysis-pack' && !this.analysisPackNavAvailable) {
        this.$bvToast.toast('Upload trade license, Emirates ID, and bank statements to access company analysis.', {
          title: 'Requirements Not Met',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }

      if (section === 'forecasting' && !this.forecastingNavAvailable) {
        this.$bvToast.toast('Complete company analysis before accessing forecasting.', {
          title: 'Requirements Not Met',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }

      if (section === 'automated-assessment-dashboard' || section === 'automated-assessment-report') {
        if (!this.hasLatestCreditModel) {
          await this.loadCreditModelAvailability();
        }
        if (!this.hasLatestCreditModel) {
          this.$bvToast.toast('Run the credit model first to access Dashboard and Report.', {
            title: 'Requirements Not Met',
            variant: 'warning',
            solid: true,
            autoHideDelay: 5000
          });
          return;
        }
      }

      this.activeSection = section;
      const routes = {
        'kyb': 'chatbot-candidate-kyb',
        'vat': 'chatbot-candidate-vat',
        'bank-statement': 'chatbot-candidate-bank-statement',
        'analysis-pack': 'chatbot-candidate-analysis-pack',
        'automated-assessment': 'chatbot-automated-assessment-input',
        'automated-assessment-dashboard': 'chatbot-automated-assessment-dashboard',
        'automated-assessment-report': 'chatbot-automated-assessment-report',
        'forecasting': 'chatbot-candidate-forecasting',
        'chat-sessions': 'chatbot-candidate',
        'overview': 'chatbot-candidate-overview'
      };
      const routeName = routes[section] || 'chatbot-candidate-overview';
      this.$router.push({
        name: routeName,
        params: { candidateId: this.selectedCandidate.id }
      }).catch(() => {});
    },
    goToUpload() {
      this.$router.push({ 
        name: 'chatbot-upload',
        params: { 
          candidateId: this.selectedCandidate.id
        }
      });
    },

    viewFiles() {
      this.$router.push({
        name: 'chatbot-candidate-files',
        params: {
          candidateId: this.selectedCandidate.id
        }
      });
    },

    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
    
    async loadOrganisationDetails() {
      if (!this.selectedCandidate?.id) {
        this.organisationDetailsLoading = false;
        return;
      }

      this.organisationDetailsLoading = true;
      this.organisationDetailsError = null;

      try {
        const details = await ChatbotService.getOrganisationDetails(this.selectedCandidate.id);
        this.organisationDetailsRaw = { ...(details || {}) };
        this.populateOrganisationForm(this.organisationDetailsRaw);
        await this.loadErpExtract();

        // Only load bank statement summary when custom balances are not enabled
        if (!this.organisationForm.useCustomBankStatementBalances) {
          await this.loadBankStatementSummary();
        }
      } catch (error) {
        console.error('Error loading organisation details:', error);
        this.organisationDetailsError = 'Failed to load organisation details';
      } finally {
        this.organisationDetailsLoading = false;
      }
    },

    async loadSectionNavRequirements() {
      if (!this.selectedCandidate?.id) return;

      try {
        const [kybRes, bankRes, analysisPackRes] = await Promise.all([
          ChatbotService.getUploads(this.selectedCandidate.id, {
            type: 'trade_license,emirates_id',
            page: 1,
            page_size: 10
          }),
          ChatbotService.getUploads(this.selectedCandidate.id, {
            type: 'bank_statement',
            page: 1,
            page_size: 10
          }),
          ChatbotService.getUploads(this.selectedCandidate.id, {
            type: 'analysis_pack',
            page: 1,
            page_size: 10
          })
        ]);

        const toList = (response) => {
          if (Array.isArray(response)) return response;
          return response?.data || response?.files || response?.uploads || [];
        };

        const kybDocs = toList(kybRes);
        const bankDocs = toList(bankRes);
        const analysisPacks = toList(analysisPackRes);

        this.sectionNavDocs = {
          tradeLicense: kybDocs.some((doc) => {
            const type = doc.type || doc.document_type;
            return type === 'trade_license' || type === 'trade_licence';
          }),
          emiratesId: kybDocs.some((doc) => (doc.type || doc.document_type) === 'emirates_id'),
          bankStatements: bankDocs.length > 0,
          analysisPack: analysisPacks.length > 0
        };
      } catch (error) {
        console.error('Error loading section navigation requirements:', error);
      }
    },

    async loadBankStatementSummary() {
      if (!this.selectedCandidate?.id) {
        return;
      }

      try {
        const summary = await ChatbotService.getBankStatementSummary(this.selectedCandidate.id);
        this.bankStatementSummary = summary;
      } catch (error) {
        console.error('Error loading bank statement summary:', error);
        this.bankStatementSummary = null;
      }
    },

    populateOrganisationForm(details) {
      this.suppressAutosave = true;
      const dateValue = details.business_incorporation_date
        || details.incorporation_date
        || details.businessIncorporationDate;
      if (dateValue) {
        const date = new Date(dateValue);
        this.organisationForm.businessIncorporationDate = Number.isNaN(date.getTime()) ? null : date;
      } else {
        this.organisationForm.businessIncorporationDate = null;
      }

      const amount = details.requested_amount ?? details.requestedAmount;
      if (amount !== undefined && amount !== null) {
        this.organisationForm.requestedAmount = amount;
      }

      const tenure = details.tenure ?? details.tenor_months ?? details.tenorMonths;
      if (tenure !== undefined && tenure !== null) {
        this.organisationForm.tenure = tenure;
      }

      const utilization = details.utilization;
      if (utilization !== undefined && utilization !== null && utilization !== '') {
        this.organisationForm.utilization = utilization;
      } else {
        this.organisationForm.utilization = '';
      }

      const rate = details.rate;
      if (rate !== undefined && rate !== null && rate !== '') {
        this.organisationForm.rate = rate;
      } else {
        this.organisationForm.rate = '';
      }

      const scoringPeriod = details.scoring_period ?? details.scoringPeriod;
      if (scoringPeriod?.end_year && scoringPeriod?.end_month) {
        this.organisationForm.scoringPeriodEndKey = `${scoringPeriod.end_year}-${Number(scoringPeriod.end_month)}`;
      } else {
        this.organisationForm.scoringPeriodEndKey = this.getDefaultScoringPeriodEndKey();
      }

      const ownerExperience = details.owner_industry_experience ?? details.ownerIndustryExperience;
      if (ownerExperience !== undefined && ownerExperience !== null && ownerExperience !== '') {
        this.organisationForm.ownerIndustryExperience = ownerExperience;
      }

      const chequeReturn = details.cheque_return_due_to_insufficient_balance ?? details.cheque_return ?? details.chequeReturn;
      if (chequeReturn !== undefined && chequeReturn !== null && chequeReturn !== '') {
        this.organisationForm.chequeReturn = chequeReturn;
      }

      const companyCreditScore = details.company_credit_score ?? details.companyCreditScore;
      if (companyCreditScore !== undefined && companyCreditScore !== null && companyCreditScore !== '') {
        this.organisationForm.companyCreditScore = companyCreditScore;
      } else {
        this.organisationForm.companyCreditScore = '';
      }

      this.organisationForm.hasAecbData = !!(
        details.has_aecb_data ?? details.hasAecbData
      );

      this.organisationForm.mainSectorType = (
        details.main_sector_type ?? details.mainSectorType ?? ''
      );

      let mainSector = details.main_sector || details.sector?.main_sector;
      let subsector = details.subsector || details.sector?.subsector;

      if (!mainSector) {
        const firstSector = (details.sectors || details.recommended_sectors || [])[0];
        if (firstSector) {
          mainSector = firstSector.main_sector;
          subsector = firstSector.subsector;
        }
      }

      if (mainSector) {
        this.organisationForm.mainSector = mainSector;
        this.organisationForm.subsector = subsector || '';
      }

      const debts = Array.isArray(details.debts) ? details.debts : null;
      if (debts) {
        const existingDebts = this.organisationForm.debts || [];
        this.organisationForm.debts = debts.map((debt, index) =>
          this.normalizeDebt(debt, existingDebts[index]?._key || null)
        );
      } else if (details.debts === null) {
        this.organisationForm.debts = [];
      }

      this.organisationForm.useCustomBankStatementBalances = !!(
        details.use_custom_bank_statement_balances ?? details.useCustomBankStatementBalances
      );
      this.organisationForm.totalBankStatementCredits = (
        details.total_bank_statement_credits ?? details.totalBankStatementCredits ?? ''
      );
      const savedBalances = details.custom_bank_statement_balances
        ?? details.customBankStatementBalances
        ?? [];
      this.populateBankBalanceMonths(savedBalances);

      this.$nextTick(() => {
        this.suppressAutosave = false;
      });
    },

    getDefaultScoringPeriodEndKey() {
      const now = new Date();
      const end = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return `${end.getFullYear()}-${end.getMonth() + 1}`;
    },

    buildScoringPeriodPayload(endKey) {
      if (!endKey) return null;

      const [yearStr, monthStr] = endKey.split('-');
      const endYear = Number(yearStr);
      const endMonth = Number(monthStr);
      if (!endYear || !endMonth) return null;

      const end = new Date(endYear, endMonth - 1, 1);
      const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);

      return {
        end_year: endYear,
        end_month: endMonth,
        start_year: start.getFullYear(),
        start_month: start.getMonth() + 1
      };
    },

    buildDefaultBankBalanceMonths() {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const endKey = this.organisationForm.scoringPeriodEndKey || this.getDefaultScoringPeriodEndKey();
      const scoringPeriod = this.buildScoringPeriodPayload(endKey);

      if (!scoringPeriod) {
        return [];
      }

      const rows = [];
      let date = new Date(scoringPeriod.start_year, scoringPeriod.start_month - 1, 1);

      for (let i = 0; i < 12; i += 1) {
        rows.push({
          month: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
          year: date.getFullYear(),
          month_number: date.getMonth() + 1,
          day_7: '',
          day_14: '',
          day_22: '',
          day_30: ''
        });
        date = new Date(date.getFullYear(), date.getMonth() + 1, 1);
      }

      return rows;
    },

    populateBankBalanceMonths(savedBalances) {
      const balances = Array.isArray(savedBalances) ? savedBalances : [];

      if (balances.length > 0) {
        this.organisationForm.customBankStatementBalances = balances.map((row) =>
          this.normalizeBankBalanceRow(row)
        );
        return;
      }

      this.organisationForm.customBankStatementBalances = this.buildDefaultBankBalanceMonths();
    },

    normalizeBankBalanceRow(row) {
      const monthAbbr = [
        'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
        'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
      ];
      const balances = row.balances || row;
      const year = row.year;
      const monthNumber = row.month_number ?? row.monthNumber;
      const month = monthNumber && year
        ? `${monthAbbr[monthNumber - 1]} ${String(year).slice(-2)}`
        : (row.month || '');

      return {
        month,
        year,
        month_number: monthNumber,
        day_7: balances.day_7 ?? '',
        day_14: balances.day_14 ?? '',
        day_22: balances.day_22 ?? '',
        day_30: balances.day_30 ?? ''
      };
    },

    formatBankBalance(value) {
      if (value === '' || value === null || value === undefined) {
        return '—';
      }

      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    normalizeDebtAmount(value) {
      if (value === '' || value === null || value === undefined) {
        return '';
      }

      const cleaned = String(value).replace(/,/g, '').trim();
      if (!cleaned) {
        return '';
      }

      const number = Number(cleaned);
      if (Number.isNaN(number)) {
        return String(value);
      }

      return number.toFixed(2);
    },

    parseDebtAmount(value) {
      if (value === '' || value === null || value === undefined) {
        return null;
      }

      const cleaned = String(value).replace(/,/g, '').trim();
      if (!cleaned) {
        return null;
      }

      const number = Number(cleaned);
      return Number.isFinite(number) ? number : null;
    },

    formatDebtAmountField(debt, field) {
      if (!debt || !field) return;
      debt[field] = this.normalizeDebtAmount(debt[field]);
    },

    onCustomBankBalanceToggle() {
      if (!this.organisationForm.useCustomBankStatementBalances) {
        this.clearBankBalancesFileInput();
        this.loadBankStatementSummary();
      } else {
        this.bankStatementSummary = null;
      }
    },

    onBankBalancesFileChange(event) {
      const file = event.target.files?.[0];
      this.bankBalancesImportFile = file || null;
    },

    clearBankBalancesFileInput() {
      this.bankBalancesImportFile = null;
      if (this.$refs.bankBalancesFileInput) {
        this.$refs.bankBalancesFileInput.value = '';
      }
    },

    async importBankBalances() {
      if (!this.selectedCandidate?.id || !this.bankBalancesImportFile || this.bankBalancesImporting) {
        return;
      }

      this.bankBalancesImporting = true;

      try {
        await ChatbotService.importBankBalances(
          this.selectedCandidate.id,
          this.bankBalancesImportFile
        );

        this.organisationForm.useCustomBankStatementBalances = true;

        const details = await ChatbotService.getOrganisationDetails(this.selectedCandidate.id);
        this.organisationDetailsRaw = { ...(details || {}) };
        this.populateOrganisationForm(this.organisationDetailsRaw);

        this.$bvToast.toast('Bank balances imported successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });

        this.clearBankBalancesFileInput();
      } catch (error) {
        console.error('Error importing bank balances:', error);
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to import bank balances. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.bankBalancesImporting = false;
      }
    },

    async deleteImportedBankBalances() {
      if (!this.selectedCandidate?.id || !this.hasImportedBankBalances || this.bankBalancesDeleting) {
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete the imported bank balances? This action cannot be undone.',
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );

      if (!confirmed) return;

      this.bankBalancesDeleting = true;
      this.suppressAutosave = true;

      try {
        this.organisationForm.customBankStatementBalances = this.buildDefaultBankBalanceMonths();
        this.organisationForm.totalBankStatementCredits = '';

        const payload = this.buildOrganisationDetailsPayload();
        payload.custom_bank_statement_balances = [];
        payload.total_bank_statement_credits = null;

        await ChatbotService.updateOrganisationDetails(
          this.selectedCandidate.id,
          payload
        );

        const details = await ChatbotService.getOrganisationDetails(this.selectedCandidate.id);
        const merged = { ...(details || payload) };
        merged.custom_bank_statement_balances = [];
        merged.customBankStatementBalances = [];
        merged.total_bank_statement_credits = null;
        this.organisationDetailsRaw = merged;
        this.populateOrganisationForm(this.organisationDetailsRaw);
        this.clearBankBalancesFileInput();

        this.$bvToast.toast('Imported bank balances deleted.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error deleting imported bank balances:', error);
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to delete imported bank balances. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.bankBalancesDeleting = false;
      }
    },

    buildBankBalancesPayload() {
      return this.organisationForm.customBankStatementBalances.map((row) => {
        const balances = {};
        ['day_7', 'day_14', 'day_22', 'day_30'].forEach((key) => {
          const value = row[key];
          balances[key] = value !== '' && value !== null && value !== undefined
            ? Number(value)
            : null;
        });

        return {
          month: row.month,
          year: row.year,
          month_number: row.month_number,
          balances
        };
      });
    },

    normalizeDebt(debt, existingKey = null) {
      return {
        _key: existingKey || (++this.debtKeyCounter),
        lender: debt.lender ?? debt.lendor ?? '',
        approval_amount: this.normalizeDebtAmount(
          debt.approval_amount ?? debt.approvalAmount ?? ''
        ),
        amount_outstanding: this.normalizeDebtAmount(
          debt.amount_outstanding ?? debt.amountOutstanding ?? ''
        ),
        repayment_interest_service: this.normalizeDebtAmount(
          debt.repayment_interest_service ?? debt.repaymentInterestService ?? ''
        )
      };
    },

    createEmptyDebt() {
      return {
        _key: ++this.debtKeyCounter,
        lender: '',
        approval_amount: '',
        amount_outstanding: '',
        repayment_interest_service: ''
      };
    },

    addDebt() {
      this.organisationForm.debts.push(this.createEmptyDebt());
    },

    removeDebt(index) {
      this.organisationForm.debts.splice(index, 1);
    },

    buildDebtsPayload() {
      return this.organisationForm.debts
        .map((debt) => {
          const lender = String(debt.lender || '').trim();
          const approvalAmount = this.parseDebtAmount(debt.approval_amount);
          const amountOutstanding = this.parseDebtAmount(debt.amount_outstanding);
          const repaymentInterestService = this.parseDebtAmount(debt.repayment_interest_service);

          const item = { lender };

          if (approvalAmount !== null) {
            item.approval_amount = approvalAmount;
          }

          if (amountOutstanding !== null) {
            item.amount_outstanding = amountOutstanding;
          }

          if (repaymentInterestService !== null) {
            item.repayment_interest_service = repaymentInterestService;
          }

          return item;
        })
        .filter((debt) =>
          debt.lender
          || debt.approval_amount !== undefined
          || debt.amount_outstanding !== undefined
          || debt.repayment_interest_service !== undefined
        );
    },

    formatOrganisationDate(date) {
      if (!date) return '';
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return '';
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    onMainSectorChange() {
      this.organisationForm.subsector = '';
    },

    async loadErpExtract() {
      if (!this.selectedCandidate?.id) return;

      try {
        const response = await ChatbotService.getUploads(this.selectedCandidate.id, {
          type: 'erp_extract',
          page: 1,
          page_size: 1
        });

        const documents = Array.isArray(response)
          ? response
          : (response.data || response.files || response.uploads || []);

        const doc = documents[0];
        this.erpExtractDoc = doc
          ? { ...doc, status: doc.scan_status || doc.status || 'pending' }
          : null;
      } catch (error) {
        console.error('Error loading ERP extract:', error);
      }
    },

    uploadErpExtract() {
      this.$router.push({
        name: 'chatbot-upload',
        params: { candidateId: this.selectedCandidate.id },
        query: { type: 'erp_extract', file_type: 'erp_extract' }
      });
    },

    viewErpExtractDocument() {
      const fileId = this.getErpExtractFileId(this.erpExtractDoc);
      if (!fileId) return;

      this.$router.push({
        name: 'chatbot-file-details',
        params: {
          candidateId: this.selectedCandidate.id,
          fileId
        }
      });
    },

    getErpExtractFileId(doc) {
      if (!doc) return null;
      return doc.id || doc.file_id || doc.upload_id;
    },

    async deleteErpExtractDocument() {
      const doc = this.erpExtractDoc;
      const fileId = this.getErpExtractFileId(doc);
      if (!fileId) {
        this.$bvToast.toast('Unable to delete document: File ID not found', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${doc.name || doc.file_name || 'this ERP Extract'}"? This action cannot be undone.`,
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );

      if (!confirmed) return;

      try {
        await ChatbotService.deleteFile(fileId);
        this.$bvToast.toast('ERP Extract deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
        await this.loadErpExtract();
      } catch (error) {
        console.error('Error deleting ERP extract:', error);
        this.$bvToast.toast('Failed to delete ERP Extract. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
      }
    },

    getStatusClass(status) {
      if (status === 'completed' || status === 'success') return 'status-completed';
      if (status === 'processing' || status === 'extracting') return 'status-processing';
      if (status === 'failed' || status === 'error') return 'status-error';
      return 'status-pending';
    },

    getStatusIcon(status) {
      if (status === 'completed' || status === 'success') return 'las la-check';
      if (status === 'processing') return 'las la-sync';
      if (status === 'extracting') return 'las la-sync';
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    },

    formatUploadDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    buildOrganisationDetailsPayload() {
      const payload = { ...this.organisationDetailsRaw };
      const {
        businessIncorporationDate,
        requestedAmount,
        tenure,
        utilization,
        rate,
        scoringPeriodEndKey,
        ownerIndustryExperience,
        chequeReturn,
        companyCreditScore,
        hasAecbData,
        mainSectorType,
        mainSector,
        subsector
      } = this.organisationForm;

      const formattedDate = this.formatOrganisationDate(businessIncorporationDate);
      if (formattedDate) {
        payload.business_incorporation_date = formattedDate;
      } else {
        delete payload.business_incorporation_date;
      }

      if (requestedAmount !== '' && requestedAmount !== null && requestedAmount !== undefined) {
        payload.requested_amount = Number(requestedAmount);
      } else {
        delete payload.requested_amount;
      }

      if (tenure !== '' && tenure !== null && tenure !== undefined) {
        payload.tenure = Number(tenure);
      } else {
        delete payload.tenure;
      }

      if (utilization !== '' && utilization !== null && utilization !== undefined) {
        payload.utilization = Number(utilization);
      } else {
        delete payload.utilization;
      }

      if (rate !== '' && rate !== null && rate !== undefined) {
        payload.rate = Number(rate);
      } else {
        delete payload.rate;
      }

      const scoringPeriod = this.buildScoringPeriodPayload(scoringPeriodEndKey);
      if (scoringPeriod) {
        payload.scoring_period = scoringPeriod;
      } else {
        delete payload.scoring_period;
      }

      if (ownerIndustryExperience !== '' && ownerIndustryExperience !== null && ownerIndustryExperience !== undefined) {
        payload.owner_industry_experience = Number(ownerIndustryExperience);
      } else {
        delete payload.owner_industry_experience;
      }

      if (chequeReturn !== '' && chequeReturn !== null && chequeReturn !== undefined) {
        payload.cheque_return_due_to_insufficient_balance = Number(chequeReturn);
      } else {
        delete payload.cheque_return_due_to_insufficient_balance;
      }

      if (companyCreditScore !== '' && companyCreditScore !== null && companyCreditScore !== undefined) {
        payload.company_credit_score = Number(companyCreditScore);
      } else {
        payload.company_credit_score = null;
      }

      payload.has_aecb_data = !!hasAecbData;

      if (mainSectorType) {
        payload.main_sector_type = mainSectorType;
      } else {
        delete payload.main_sector_type;
      }

      if (mainSector) {
        payload.main_sector = mainSector;
        payload.subsector = subsector || '';
      } else {
        delete payload.main_sector;
        delete payload.subsector;
      }

      delete payload.sectors;
      delete payload.recommended_sectors;

      payload.debts = this.buildDebtsPayload();

      payload.use_custom_bank_statement_balances = this.organisationForm.useCustomBankStatementBalances;
      if (this.organisationForm.useCustomBankStatementBalances) {
        const totalCredits = this.organisationForm.totalBankStatementCredits;
        if (totalCredits !== '' && totalCredits !== null && totalCredits !== undefined) {
          payload.total_bank_statement_credits = Number(totalCredits);
        } else {
          payload.total_bank_statement_credits = null;
        }

        if (this.hasImportedBankBalances) {
          payload.custom_bank_statement_balances = this.buildBankBalancesPayload();
        } else {
          delete payload.custom_bank_statement_balances;
        }
      } else {
        delete payload.custom_bank_statement_balances;
        delete payload.total_bank_statement_credits;
      }

      return payload;
    },

    queueAutosave() {
      this.autosaveStatus = 'saving';
      // If a save is already in flight, mark a follow-up so we don't clobber
      // newer field edits when the response is applied.
      if (this.organisationDetailsSaving) {
        this.autosaveResaveQueued = true;
      }
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
      }
      this.autosaveTimer = setTimeout(() => {
        this.autosaveTimer = null;
        this.saveOrganisationDetails();
      }, 1500);
    },

    async saveOrganisationDetails() {
      if (!this.selectedCandidate?.id) return;

      // A save is already running; re-run once it finishes so we persist the latest edits.
      if (this.organisationDetailsSaving) {
        this.autosaveResaveQueued = true;
        return;
      }

      this.organisationDetailsSaving = true;
      this.autosaveStatus = 'saving';
      this.organisationDetailsError = null;

      try {
        const payload = this.buildOrganisationDetailsPayload();
        const saved = await ChatbotService.updateOrganisationDetails(this.selectedCandidate.id, payload);
        const merged = { ...(saved || {}) };

        // Some organisation-details responses omit debts; keep what we just saved.
        if (!Array.isArray(merged.debts) && Array.isArray(payload.debts)) {
          merged.debts = payload.debts;
        }

        this.organisationDetailsRaw = merged;
        // Don't overwrite in-progress edits if another save is queued or debounce is pending.
        if (!this.autosaveResaveQueued && !this.autosaveTimer) {
          this.populateOrganisationForm(this.organisationDetailsRaw);
        }
        this.autosaveStatus = 'saved';
      } catch (error) {
        console.error('Error saving organisation details:', error);
        this.organisationDetailsError = error.response?.data?.message || 'Failed to save organisation details';
        this.autosaveStatus = 'error';
      } finally {
        this.organisationDetailsSaving = false;
        if (this.autosaveResaveQueued) {
          this.autosaveResaveQueued = false;
          this.$nextTick(() => this.queueAutosave());
        }
      }
    },

    async loadCreditModelAvailability() {
      if (!this.selectedCandidate?.id) return;

      try {
        const filesData = await ChatbotService.getUploads(this.selectedCandidate.id, {
          page: 1,
          page_size: 1,
          type: 'credit_model'
        });
        const list = Array.isArray(filesData)
          ? filesData
          : (filesData?.data || filesData?.files || filesData?.uploads || []);
        this.hasLatestCreditModel = (filesData?.count > 0) || list.length > 0;
      } catch (error) {
        console.error('Error checking credit model availability:', error);
      }
    },

    async loadCreditModelScore() {
      if (!this.selectedCandidate || !this.selectedCandidate.id) {
        this.creditModelError = 'No candidate selected';
        return;
      }

      this.creditModelLoading = true;
      this.creditModelError = null;
      this.hasLatestCreditModel = false;

      try {
        const filesData = await ChatbotService.getUploads(this.selectedCandidate.id, {
          page: 1,
          page_size: 10,
          type: 'credit_model'
        });

        console.log('filesData', filesData);
        if (filesData.count === 0) {
          this.creditModelScore = null;
          this.hasLatestCreditModel = false;
          return;
        }

        this.hasLatestCreditModel = true;

        const fileId = filesData.data[0].id;

        const fileDetails = await ChatbotService.getFileDetails(fileId);

        const creditModelData = fileDetails.transactions[0].risk_rating_data;
        this.creditModelScore = {};

        if (creditModelData.Summary) {
          const summary = this.resolveSummaryGrandTotal(creditModelData.Summary);
          if (summary) {
            this.creditModelScore.Summary = summary;
          }
          Object.assign(
            this.creditModelScore,
            this.parseSummaryCategories(creditModelData.Summary)
          );
        }

        Object.entries(creditModelData).forEach(([key, value]) => {
          if (key === 'Summary') {
            return;
          }

          let categoryKey = key;
          if (this.checknoAECB(value[0])) {
            categoryKey = `${key}(No AECB)`;
          }

          if (this.creditModelScore[categoryKey] || this.creditModelScore[key]) {
            return;
          }

          const categoryRow = value.find(
            (item) => item[categoryKey] === `${this.trimNoAECB(categoryKey)} Risk`
          );

          if (categoryRow) {
            this.creditModelScore[categoryKey] = {
              Weights: categoryRow.Weights || 0,
              ObtainedScore: categoryRow['Obtained Score'] || 0
            };
          }
        });

        if (!this.creditModelScore.Summary) {
          this.creditModelScore = null;
        }
      } catch (error) {
        console.error('Error loading credit model score:', error);
        this.creditModelError = 'Unable to load credit model score';
      } finally {
        this.creditModelLoading = false;
      }
    },

    resolveSummaryGrandTotal(summaryItems) {
      if (!Array.isArray(summaryItems)) return null;

      const validItems = summaryItems.filter((item) => item?.Risk);

      const recalculatedGrandTotal = validItems.find((item) => {
        const risk = String(item.Risk);
        return risk.includes('Grand Total') && risk.includes('Recalculated');
      });

      if (recalculatedGrandTotal) {
        return {
          totalScore: recalculatedGrandTotal['Total Score'],
          rating: recalculatedGrandTotal['Obtained Score']
        };
      }

      const grandTotal = validItems.find((item) => item.Risk === 'Grand Total');
      if (grandTotal) {
        return {
          totalScore: grandTotal['Total Score'],
          rating: grandTotal['Obtained Score']
        };
      }

      return null;
    },

    parseSummaryCategories(summaryItems) {
      if (!Array.isArray(summaryItems)) return {};

      return summaryItems
        .filter((item) => item?.Risk && !this.isGrandTotalRisk(item.Risk))
        .reduce((categories, item) => {
          categories[item.Risk] = {
            Weights: item['Total Score'] ?? 0,
            ObtainedScore: item['Obtained Score'] ?? 0
          };
          return categories;
        }, {});
    },

    isGrandTotalRisk(risk) {
      return String(risk).includes('Grand Total');
    },

    trimNoAECB(value) {
      if (!value) return '';
      return value.replace(/\(No AECB\)/g, '').trim();
    },

    checknoAECB(value) {
      if (!value || typeof value !== 'object') {
        return false;
      }
      return Object.keys(value).some(key => key.includes('No AECB'));
    },
    
    truncateMessage(message, maxLength = 60) {
      if (!message) return '';
      if (message.length <= maxLength) return message;
      return message.substring(0, maxLength) + '...';
    },

    formatCreditScoreNumber(value) {
      if (value === null || value === undefined || value === '') return 'N/A';
      if (typeof value === 'number') {
        return value.toLocaleString('en-US', {
          minimumFractionDigits: value % 1 !== 0 ? 1 : 0,
          maximumFractionDigits: 1
        });
      }
      return value;
    },

    getNormalizedRiskScore(obtainedScore, totalScore) {
      const obtained = Number(obtainedScore);
      const total = Number(totalScore);

      if (!Number.isFinite(obtained) || !Number.isFinite(total) || total <= 0) {
        return null;
      }

      return Math.min(100, Math.max(0, (obtained / total) * 100));
    },

    formatCreditScorePercentage(obtainedScore, totalScore) {
      const score = this.getNormalizedRiskScore(obtainedScore, totalScore);
      if (score === null) return 'N/A';
      return `${Number.isInteger(score) ? score : score.toFixed(1)}%`;
    },

    getRiskRatingLabel(obtainedScore, totalScore) {
      const score = this.getNormalizedRiskScore(obtainedScore, totalScore);

      if (score === null) return 'Not rated';
      if (score >= 90) return 'Very Low Risk';
      if (score >= 80) return 'Low Risk';
      if (score >= 75) return 'Moderately Low Risk';
      if (score >= 70) return 'Moderate Risk';
      if (score >= 50) return 'High Risk';
      return 'Unacceptable';
    },

    getRiskRatingClass(obtainedScore, totalScore) {
      const score = this.getNormalizedRiskScore(obtainedScore, totalScore);

      if (score === null) return 'risk-rating-unrated';
      if (score >= 90) return 'risk-rating-very-low';
      if (score >= 80) return 'risk-rating-low';
      if (score >= 75) return 'risk-rating-moderately-low';
      if (score >= 70) return 'risk-rating-moderate';
      if (score >= 50) return 'risk-rating-high';
      return 'risk-rating-unacceptable';
    },

    getRiskGaugeAngle(obtainedScore, totalScore) {
      const score = this.getNormalizedRiskScore(obtainedScore, totalScore);
      const normalized = score === null ? 0 : score;
      return 180 + (normalized * 1.8);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.section-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #101828;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.7rem;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 1.25rem;
    border-radius: 999px;
    background: linear-gradient(180deg, #0f9d58, #16a34a);
  }
}

.chat-session-selector {
  width: 100%;
  min-height: 100%;
  background: #eef1f6;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow: visible;
  margin: 0;
  gap: 20px;
  padding: 20px;
}

.candidate-sidebar {
  width: 224px;
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 8px 24px -12px rgba(16, 24, 40, 0.14);
  flex-shrink: 0;
  padding: 1rem 0;
  position: sticky;
  // Fixed top header is 76px tall — offset below it plus a little breathing room
  top: 92px;
  align-self: flex-start;
  max-height: calc(100vh - 92px - 20px);
  overflow-y: auto;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.75rem;
}

.nav-group {
  display: flex;
  flex-direction: column;
}

.nav-sub-items {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  margin: 0.1rem 0 0.25rem;
  padding-left: 1rem;
  border-left: 2px solid #e6e9f0;
  margin-left: 1.25rem;
}

.nav-sub-item {
  padding: 0.45rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #718096;
  font-family: $brand-font-medium;
  cursor: pointer;
  transition: all 0.15s ease;
  text-decoration: none;

  &:hover {
    color: #121926;
    background: #f0f3f8;
  }

  &.active {
    color: #0c7f47;
    font-weight: 600;
    background: #e9f9f0;
  }

  &.locked {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      color: #718096;
      background: transparent;
    }
  }
}


.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  color: #4a5568;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  font-family: $brand-font-medium;
  transition: all 0.2s ease;
  cursor: pointer;
  
  i {
    font-size: 1.25rem;
    color: #718096;
    width: 20px;
    text-align: center;
  }
  
  span {
    flex: 1;
  }
  
  &:hover {
    background: #f0f3f8;
    color: #121926;

    i {
      color: #0f9d58;
    }
  }

  &.active {
    background: #e9f9f0;
    color: #0c7f47;
    font-weight: 600;
    border-left: 3px solid #0f9d58;
    padding-left: calc(1rem - 3px);

    i {
      color: #0f9d58;
    }
  }

  &.locked {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: transparent;
      color: #4a5568;

      i {
        color: #718096;
      }
    }
  }
}

.candidate-main-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  gap: 20px;
}

.candidate-main-content--overflow-visible {
  overflow: visible;
}

.section-content-forecasting {
  overflow: visible;
}

.section-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: visible;
}

.subpage-card {
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 12px 32px -18px rgba(16, 24, 40, 0.18);
  overflow: hidden;
  min-height: 60vh;
}

.kyb-placeholder {
  padding: 2rem;
  min-height: 100%;
}

.candidate-header {
  padding: 1.75rem 2rem;
  border: 1px solid #e6e9f0;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdfe 100%);
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 12px 32px -18px rgba(16, 24, 40, 0.18);
}

.credit-rating-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 2rem;
}

.credit-rating-panel {
  flex: 1;
  min-width: 0;
  display: flex;
}

.view-files-action {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.candidate-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.candidate-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, $color-primary, darken($color-primary, 15%));
  color: $color-secondary;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.candidate-details {
  width: 100%;
  flex: 1;
  display: flex;
}

.candidate-details h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
  line-height: 1.2;
}

.credit-model-card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.25rem;
  min-height: auto;
  width: 100%;
  flex: 1;
  transition: none;
  font-family: $brand-font-regular;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 1.75rem;

  &.empty {
    cursor: default;
    align-items: center;
  }
}

.credit-model-loading {
  justify-content: center;

  .credit-model-content {
    flex: 0 0 auto;
    justify-content: center;
  }
}

.credit-model-content {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.credit-model-card.success .credit-model-content {
  flex: 0 0 auto;
  min-width: 440px;
  padding-right: 2rem;
  border-right: 1px solid #e2e8f0;
}

.credit-model-gauge-panel {
  align-items: flex-start;
}

.credit-model-gauge-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.credit-model-gauge-body {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.credit-model-gauge-info {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
  min-width: 160px;
}

.risk-gauge {
  position: relative;
  top: 8px;
  left: -13px;
  flex: 0 0 240px;
  width: 240px;
  max-width: 100%;
  margin: 0;
}

.risk-gauge-dial {
  position: relative;
  width: 240px;
  max-width: 100%;
  height: 130px;
  margin: 0 auto;
  overflow: hidden;
}

.risk-gauge-arc {
  position: absolute;
  top: 0;
  left: 0;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: conic-gradient(
    from 270deg,
    #ef4444 0deg 90deg,
    #f97316 90deg 126deg,
    #f59e0b 126deg 135deg,
    #facc15 135deg 144deg,
    #84cc16 144deg 162deg,
    #16a34a 162deg 180deg,
    transparent 180deg 360deg
  );
}

.risk-gauge-mask {
  position: absolute;
  z-index: 2;
  top: 42px;
  left: 42px;
  width: 156px;
  height: 156px;
  border-radius: 50%;
  background: #ffffff;
}

.risk-gauge-needle {
  position: absolute;
  z-index: 3;
  left: 50%;
  top: 118px;
  width: 82px;
  height: 3px;
  border-radius: 999px;
  background: #1e3a5f;
  transform-origin: 0 50%;
  transition: transform 0.5s ease;
}

.risk-gauge-hub {
  position: absolute;
  z-index: 4;
  left: calc(50% - 9px);
  top: 110px;
  width: 18px;
  height: 18px;
  border: 5px solid #1e3a5f;
  border-radius: 50%;
  background: #ffffff;
  box-sizing: border-box;
}

.risk-gauge-scale {
  display: flex;
  justify-content: space-between;
  margin-top: -0.1rem;
  color: #94a3b8;
  font-family: $brand-font-medium;
  font-size: 0.9rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.credit-model-final-main {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0;
}

.credit-model-final-score {
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;

  strong {
    color: $color-primary;
    font-size: 2.25rem;
    font-weight: 700;
    letter-spacing: -0.5px;

    &.risk-rating-very-low { color: #16a34a; }
    &.risk-rating-low { color: #65a30d; }
    &.risk-rating-moderately-low { color: #ca8a04; }
    &.risk-rating-moderate { color: #d97706; }
    &.risk-rating-high { color: #ea580c; }
    &.risk-rating-unacceptable { color: #ef4444; }
    &.risk-rating-unrated { color: #64748b; }
  }

  span {
    color: #64748b;
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.risk-rating-badge {
  display: inline-block;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  color: #ffffff;
  font-family: $brand-font-semi-bold;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 8px -2px rgba(16, 24, 40, 0.25);

  &.risk-rating-very-low { background: #16a34a; }
  &.risk-rating-low { background: #65a30d; }
  &.risk-rating-moderately-low { background: #ca8a04; }
  &.risk-rating-moderate { background: #d97706; }
  &.risk-rating-high { background: #ea580c; }
  &.risk-rating-unacceptable { background: #ef4444; }
  &.risk-rating-unrated { background: #64748b; }
}

.credit-model-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.credit-model-cta-button {
  align-self: flex-start;
}

.credit-model-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: $brand-font-medium;
  line-height: 1.2;
}

.credit-model-status {
  font-size: 1.125rem;
  font-weight: 500;
  color: #4b5563;
  font-family: $brand-font-regular;
  line-height: 1.2;
}

.credit-model-rating {
  display: flex;
  align-items: baseline;
  gap: 0.5625rem;
  line-height: 1;
}

.rating-value {
  font-size: 2.025rem;
  font-weight: 700;
  color: #059669;
  font-family: $brand-font-medium;
  line-height: 1;
}

.rating-score {
  font-size: 1.125rem;
  font-weight: 500;
  color: #6b7280;
  font-family: $brand-font-regular;
  line-height: 1;
}

.credit-model-cta {
  font-size: 1.0125rem;
  font-weight: 500;
  color: $color-primary;
  font-family: $brand-font-medium;
  line-height: 1.2;
}

.credit-model-details {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
  min-width: 0;
  padding: 0 0.25rem;
}

.credit-model-category {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0.35rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
}

.category-header {
  display: flex;
  align-items: center;
  min-width: 0;
}

.category-name {
  font-size: 0.9rem;
  font-weight: 400;
  color: #4b5563;
  font-family: $brand-font-regular;
  text-transform: none;
  line-height: 1.2;
}

.category-scores {
  display: flex;
  flex-shrink: 0;
}

.score-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.675rem;
}

.score-label {
  font-size: 0.84375rem;
  font-weight: 500;
  color: #6b7280;
  font-family: $brand-font-regular;
}

.score-value {
  font-size: 0.9rem;
  font-weight: 400;
  font-family: $brand-font-regular;
}

.score-obtained {
  color: #0f172a;
  font-weight: 700;
}

.score-total {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 400;
}

.candidate-company {
  margin: 0;
  font-size: 1rem;
  color: #718096;
  font-family: $brand-font-regular;
}

.sessions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  h4 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: #2d3748;
    font-family: $brand-font-medium;
  }
}

.autosave-status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: $brand-font-medium;
  color: #718096;
  min-height: 1.25rem;
  transition: color 0.2s ease;

  i {
    font-size: 1rem;
  }

  &.saving {
    color: #718096;
  }

  &.saved {
    color: $color-primary;
  }

  &.error {
    color: #e53e3e;
  }
}

.loading {
  text-align: center;
  color: #718096;
  padding: 3rem 2rem;
  font-style: italic;
  font-family: $brand-font-regular;
}

.organisation-details-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  font-style: normal;
}

.error-message {
  text-align: center;
  color: #e53e3e;
  padding: 1rem 2rem;
  font-size: 0.9rem;
  font-family: $brand-font-regular;
  background: #fed7d7;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  margin: 1rem 0;
}

// Responsive design
@media (max-width: 768px) {
  .chat-session-selector {
    margin: 0;
    flex-direction: column;
  }
  
  .candidate-sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    padding: 1rem 0;
    position: static;
  }
  
  .sidebar-nav {
    flex-direction: row;
    overflow-x: auto;
    padding: 0 1rem;
    gap: 0.5rem;
    
    .nav-item {
      white-space: nowrap;
      min-width: fit-content;
    }
  }
  
  .candidate-header {
    padding: 1.5rem;
  }

  .credit-rating-row {
    flex-direction: column;
  }
  
  .candidate-info {
    gap: 1rem;
  }
  
  .candidate-avatar {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  
  .candidate-details h3 {
    font-size: 1.3rem;
  }
}

// Organisation Details Section
.organisation-details-section {
  padding: 1.75rem 2rem;
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 12px 32px -18px rgba(16, 24, 40, 0.18);
  overflow: visible;

  .sessions-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #eef1f6;
  }
}

.organisation-details-content {
  overflow: visible;

  .form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem 1.75rem;

    &.sector-row {
      margin-top: 1.5rem;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;

      &.sector-row {
        grid-template-columns: 1fr;
      }
    }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-label {
    position: relative;
    top: 5px;
    left:5px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.045em;
    color: #667085;
    font-family: $brand-font-semi-bold;
  }

  .form-input {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d3d9e4;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 500;
    color: #121926;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      outline: none;
      border-color: #0f9d58;
      box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.16);
    }

    &::placeholder {
      color: #98a2b3;
      font-weight: 400;
    }

    &.date-input {
      cursor: pointer;
    }

    &.field-highlight {
      border-color: #2874E9;
      box-shadow: 0 0 0 4px rgba(40, 116, 233, 0.22);
      animation: requested-amount-highlight 1.2s ease-in-out 2;
    }
  }

  @keyframes requested-amount-highlight {
    0%, 100% {
      box-shadow: 0 0 0 4px rgba(40, 116, 233, 0.22);
    }
    50% {
      box-shadow: 0 0 0 7px rgba(40, 116, 233, 0.12);
    }
  }

  .rate-display-card {
    width: 100%;
    height: 37.5px;
    display: flex;
    align-items: center;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d3d9e4;
    border-radius: 8px;
    box-sizing: border-box;
    font-size: 0.95rem;
    font-weight: 500;
    background: #f8fafc;
    overflow: hidden;
  }

  .rate-display-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #121926;
    line-height: 1.2;
  }

  .rate-display-inline {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
  }

  .rate-display-placeholder {
    font-size: 0.95rem;
    line-height: 1.2;
    color: #98a2b3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rate-display-note-inline {
    font-size: 0.82rem;
    line-height: 1;
    color: #667085;
    white-space: nowrap;
  }

  .date-input-wrapper {
    position: relative;
    width: 100%;
  }

  .org-debts-section {
    margin-top: 1.5rem;
  }

  .org-bank-balances-section {
    margin-top: 2.5rem;
  }

  .org-bank-balances-fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .bank-credits-field {
    margin-top: 1rem;
    max-width: 320px;
  }

  .form-static-value {
    position: relative;
    left: 13px;
    display: block;
    padding: 0.625rem 0;
    font-size: 1.05rem;
    font-weight: 600;
    font-family: $brand-font-semi-bold;
    color: #121926;
    font-variant-numeric: tabular-nums;
  }

  .bank-balances-import {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .bank-balances-import-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .bank-balances-file-input-container {
    position: relative;
    flex: 1;
    min-width: 240px;
    max-width: 360px;
    height: 42px;
    border: 1px dashed #cbd5e0;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: #f8fafc;
    transition: border-color 0.2s ease, background 0.2s ease;

    &:hover {
      border-color: $color-primary;
      background: rgba($color-primary, 0.05);
    }
  }

  .bank-balances-file-input {
    position: absolute;
    inset: 0;
    z-index: 1;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  .bank-balances-file-input-overlay {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0 0.875rem;
    pointer-events: none;
    overflow: hidden;
    max-width: 100%;
  }

  .bank-balances-file-icon {
    flex-shrink: 0;
    font-size: 1.125rem;
    color: $color-primary;
  }

  .bank-balances-file-hint {
    font-size: 0.8125rem;
    color: #718096;
    font-family: $brand-font-regular;
    white-space: nowrap;
  }

  .bank-balances-file-selected {
    font-size: 0.8125rem;
    color: #2d3748;
    font-family: $brand-font-medium;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .bank-balances-table-wrapper {
    overflow-x: auto;
  }

  .bank-balances-table {
    width: 100%;
    border-collapse: collapse;
    background: transparent;

    thead {
      background: transparent;
      border-bottom: none;

      th {
        padding: 0.75rem 0.875rem;
        padding-left: 1.1rem;
        text-align: left;
        font-size: 0.825rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-family: $brand-font-medium;
        border: none;
      }
    }

    tbody {
      tr {
        border-bottom: none;
        background: transparent;
      }

      td {
        padding: 0.625rem 0.875rem;
        padding-left: 1.1rem;
        font-size: 0.875rem;
        color: #2d3748;
        border: none;
      }
    }

    .month-cell {
      font-size: 0.89375rem;
      font-weight: 500;
      white-space: nowrap;
      font-family: $brand-font-medium;
    }
  }

  .org-bank-balances-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .bank-balance-toggle {
    position: relative;
    left: 7px;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    cursor: pointer;
    margin: 0;

    input[type='checkbox'] {
      width: 1rem;
      height: 1rem;
      cursor: pointer;
      accent-color: $color-primary;
    }

    .form-label {
      position: static;
      margin: 0;
      cursor: pointer;
    }
  }

  .aecb-data-toggle {
    position: relative;
    top: 17px;
    left: 7px;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    cursor: pointer;
    margin-top: 1.75rem;

    input[type='checkbox'] {
      width: 1rem;
      height: 1rem;
      cursor: pointer;
      accent-color: $color-primary;
    }

    .form-label {
      position: static;
      margin: 0;
      cursor: pointer;
    }
  }

  .org-debts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    .form-label {
      font-size: 1.05rem;
      font-weight: 600;
      color: #2d3748;
    }
  }

  .debts-table-wrapper {
    background: transparent;
    border: none;
    border-radius: 0;
    overflow-x: auto;
    box-shadow: none;
  }

  .debts-table {
    width: 100%;
    border-collapse: collapse;
    background: transparent;

    thead {
      background: transparent;
      border-bottom: none;

      th {
        padding: 0.75rem 0.875rem;
        padding-left: 1.1rem;
        text-align: left;
        font-size: 0.825rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-family: $brand-font-medium;
        border: none;

        &.col-actions {
          width: 48px;
          text-align: center;
        }
      }
    }

    tbody {
      .debt-row {
        border-bottom: none;
        background: transparent;

        &:hover {
          background-color: transparent;
        }

        &:last-child {
          border-bottom: none;
        }
      }

      td {
        padding: 0.5rem 0.875rem;
        vertical-align: middle;
        border: none;
        background: transparent;

        &.col-actions {
          text-align: center;
          width: 48px;
        }
      }
    }

    .debts-empty-cell {
      padding: 1rem;
      text-align: center;
      color: #718096;
      font-size: 0.9625rem;
    }

    .debt-input {
      width: 100%;
      padding: 0.5rem 0.625rem;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      font-size: 0.891rem;
      color: #2d3748;
      background: #ffffff;
      margin: 0;

      &:focus {
        outline: none;
        border-color: $color-primary;
      }
    }
  }

  .remove-debt-btn {
    color: #dc2626;

    i {
      font-size: 1.3rem;
    }

    &:hover i {
      color: darken(#dc2626, 10%);
    }
  }

  .org-erp-section {
    margin-top: 1.5rem;
  }

  .org-erp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .document-card {
    background: #ffffff;
    border: 1px solid #e6e9f0;
    border-radius: 12px;
    padding: 0.85rem 1rem;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;

    &:hover {
      border-color: #cdd5e0;
      box-shadow: 0 6px 18px -8px rgba(16, 24, 40, 0.18);
    }
  }

  .document-card-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  .document-icon-large {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.25rem;
      color: $color-primary;
    }
  }

  .document-name {
    flex: 1;
    font-size: 1rem;
    font-weight: 500;
    color: #2d3748;
    font-family: $brand-font-medium;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .document-date {
    font-size: 0.875rem;
    color: #718096;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .empty-document {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px dashed #e2e8f0;

    i {
      font-size: 0.875rem;
      color: #cbd5e0;
    }

    p {
      color: #718096;
      font-size: 0.875rem;
      margin: 0;
    }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    font-size: 1rem;
    background: transparent;

    &.status-completed {
      color: #16a34a;
      i { color: #16a34a; }
    }

    &.status-processing {
      color: #3b82f6;
      i { color: #3b82f6; }
    }

    &.status-error {
      color: #dc2626;
      i { color: #dc2626; }
    }

    &.status-pending {
      color: #6b7280;
      i { color: #6b7280; }
    }

    i {
      font-size: 1.3rem;
    }
  }

  .view-btn {
    background: transparent;
    border: none;
    color: #4a5568;
    padding: 0.1rem;
    margin-right: 0.2rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
    flex-shrink: 0;

    &:hover i {
      color: darken(#4a5568, 10%);
    }

    i {
      font-size: 1.3rem;
    }
  }

  .delete-btn {
    background: transparent;
    border: none;
    color: #dc2626;
    padding: 0.1rem;
    margin-right: 0.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
    flex-shrink: 0;

    i {
      font-size: 1.3rem;
    }

    &:hover i {
      color: darken(#dc2626, 10%);
    }
  }
}

</style> 